const INTERNAL_HOSTS = new Set([
  "modelaiagency.com",
  "www.modelaiagency.com",
  "127.0.0.1",
  "localhost",
]);

export const normalizeAnalyticsUrl = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const getHostnameFromUrl = (value: string | null | undefined): string | null => {
  try {
    const normalized = normalizeAnalyticsUrl(value);
    if (!normalized) return null;
    return new URL(normalized).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
};

export const isInternalAnalyticsHost = (value: string | null | undefined): boolean => {
  const hostname = getHostnameFromUrl(value);
  if (!hostname) return false;
  if (INTERNAL_HOSTS.has(hostname)) return true;
  return hostname.endsWith(".modelaiagency.com");
};

export const isInternalTrafficSource = (source: string | null | undefined): boolean => {
  const normalized = (source || "").trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === "direct") return false;
  if (normalized.startsWith("site:")) {
    return isInternalAnalyticsHost(normalized.slice(5));
  }
  if (normalized.includes("127.0.0.1") || normalized.includes("localhost")) return true;
  if (normalized.includes("modelaiagency.com")) return true;
  return isInternalAnalyticsHost(normalized);
};

export const getNormalizedTrafficSource = (
  utmSource: string | null | undefined,
  referrer: string | null | undefined,
  userAgent?: string | null,
) => {
  const source = (utmSource || "").trim().toLowerCase();
  if (source) {
    if (source === "ig" || source.includes("instagram")) return "instagram.com";
    if (source === "fb" || source.includes("facebook")) return "facebook.com";
    if (source === "x" || source === "tw" || source.includes("twitter")) return "x.com";
    if (source === "tt" || source.includes("tiktok")) return "tiktok.com";
    if (source === "yt" || source.includes("youtube")) return "youtube.com";
    if (source === "tg" || source.includes("telegram")) return "telegram.org";
    if (source.includes("linkedin")) return "linkedin.com";
    if (source.includes("reddit")) return "reddit.com";
    if (source.includes("threads")) return "threads.net";
    if (source.includes("google")) return "google.com";
    if (source.includes("chatgpt") || source.includes("openai") || source === "gpt") return "chatgpt.com";
    if (source.includes("email")) return "email";
    return source;
  }

  const host = getHostnameFromUrl(referrer);
  if (host && !isInternalAnalyticsHost(referrer)) {
    if (host === "l.instagram.com") return "instagram.com";
    if (host === "t.co") return "x.com";
    if (host === "youtu.be") return "youtube.com";
    if (host === "t.me") return "telegram.org";
    return host;
  }

  const ua = (userAgent || "").toLowerCase();
  if (ua.includes("instagram")) return "instagram.com";
  if (ua.includes("fban") || ua.includes("fbav") || ua.includes("facebook")) return "facebook.com";
  if (ua.includes("tiktok")) return "tiktok.com";
  if (ua.includes("telegram")) return "telegram.org";
  if (ua.includes("twitter") || ua.includes("x.com")) return "x.com";
  if (ua.includes("youtube")) return "youtube.com";

  return "direct";
};

export const getTrafficSourceLabel = (source: string | null | undefined): string => {
  const normalized = (source || "").trim().toLowerCase();
  if (!normalized || normalized === "direct") return "Direct / Unknown";

  const labels: Record<string, string> = {
    "facebook.com": "Facebook",
    "instagram.com": "Instagram",
    "x.com": "X",
    "tiktok.com": "TikTok",
    "youtube.com": "YouTube",
    "telegram.org": "Telegram",
    "linkedin.com": "LinkedIn",
    "reddit.com": "Reddit",
    "threads.net": "Threads",
    "google.com": "Google",
    "chatgpt.com": "ChatGPT",
    email: "Email",
  };

  return labels[normalized] || normalized;
};

export const getTrafficSourceSecondaryLabel = (source: string | null | undefined): string | null => {
  const normalized = (source || "").trim().toLowerCase();
  if (!normalized || normalized === "direct") return null;

  const labels: Record<string, string> = {
    "facebook.com": "facebook.com",
    "instagram.com": "instagram.com",
    "x.com": "x.com",
    "tiktok.com": "tiktok.com",
    "youtube.com": "youtube.com",
    "telegram.org": "telegram.org",
    "linkedin.com": "linkedin.com",
    "reddit.com": "reddit.com",
    "threads.net": "threads.net",
    "google.com": "google.com",
    "chatgpt.com": "chatgpt.com",
    email: "Email",
  };

  return labels[normalized] || "External website";
};

export const getCountryFlag = (countryCode: string | null | undefined): string => {
  const code = (countryCode || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "🌍";
  return String.fromCodePoint(...[...code].map((char) => 127397 + char.charCodeAt(0)));
};

export const getCountryName = (countryCode: string | null | undefined, fallback?: string | null): string => {
  const code = (countryCode || "").trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(code)) {
    try {
      const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
      return displayNames.of(code) || fallback || code;
    } catch {
      return fallback || code;
    }
  }
  return fallback || "Unknown";
};

export const getDeviceOs = (userAgent: string | null | undefined): string => {
  const ua = (userAgent || "").toLowerCase();
  if (!ua) return "Unknown";
  if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod")) return "iOS";
  if (ua.includes("android")) return "Android";
  if (ua.includes("windows")) return "Windows";
  if (ua.includes("mac os") || ua.includes("macintosh")) return "Mac";
  if (ua.includes("linux")) return "Linux";
  return "Unknown";
};

export const getDeviceBrowser = (userAgent: string | null | undefined): string => {
  const ua = (userAgent || "").toLowerCase();
  if (!ua) return "Unknown";
  if (ua.includes("edg/")) return "Edge";
  if (ua.includes("opr/") || ua.includes("opera")) return "Opera";
  if (ua.includes("crios") || ua.includes("chrome/")) return "Chrome";
  if (ua.includes("fxios") || ua.includes("firefox/")) return "Firefox";
  if (ua.includes("safari/") && !ua.includes("chrome/") && !ua.includes("crios") && !ua.includes("android")) return "Safari";
  if (ua.includes("samsungbrowser")) return "Samsung Internet";
  if (ua.includes("instagram")) return "Instagram";
  if (ua.includes("fbav") || ua.includes("fban")) return "Facebook";
  return "Unknown";
};

export const getDevicePlatform = (userAgent: string | null | undefined): string => {
  const ua = (userAgent || "").toLowerCase();
  if (!ua) return "Unknown";
  if (ua.includes("iphone") || ua.includes("android") || ua.includes("mobile")) return "Mobile";
  if (ua.includes("ipad") || ua.includes("tablet")) return "Tablet";
  return "Desktop";
};
